import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, Check, X, Eye, RefreshCw, CheckCircle, 
  AlertTriangle, Trash2, User, Clock, MapPin, DollarSign,
  Package, MessageSquare, Search, Filter, Edit, Archive, CalendarClock, Hash, Car, Ban, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api, { bookingsAPI, ridesAPI } from '../../services/api';
import { NoteIconButton } from './NotesModal';
import BookingDetailModal from './BookingDetailModal';

const BookingsTab = ({
  bookings,
  bookingStatusTab,
  setBookingStatusTab,
  bulkSelectMode,
  setBulkSelectMode,
  selectedBookingIds,
  setSelectedBookingIds,
  setShowCreateBookingModal,
  setSelectedBooking,
  setShowAssignModal,
  handleBookingAction,
  handleOpenReschedule,
  handleViewBookingDetails,
  noteCounts,
  setNotesModal,
  fetchAllData,
  handleAssignToOwner,
  handleBatchAssignToOwner,  // New prop for batch self-assignment
  setShowCreateRideModal  // New prop for creating rides
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [detailModalBooking, setDetailModalBooking] = useState(null);
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, booking: null });
  const [editData, setEditData] = useState({});
  
  // Delete/Cancel confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ 
    open: false, 
    booking: null, 
    action: null, // 'cancel' or 'delete'
    reason: '',
    loading: false
  });
  
  const toggleBookingSelection = (bookingId) => {
    setSelectedBookingIds(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  // Handle cancel booking (soft delete - keeps record)
  const handleCancelBooking = async () => {
    if (!deleteModal.booking) return;
    
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      const booking = deleteModal.booking;
      if (booking.is_ride) {
        await ridesAPI.cancel(booking.id, deleteModal.reason || 'Cancelled by owner');
      } else {
        await bookingsAPI.manage(booking.id, { 
          status: 'cancelled', 
          reason: deleteModal.reason || 'Cancelled by owner',
          notify_customer: true 
        });
      }
      toast.success(`${booking.is_ride ? 'Ride' : 'Booking'} cancelled successfully`);
      setDeleteModal({ open: false, booking: null, action: null, reason: '', loading: false });
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle permanent delete (hard delete - removes from database)
  const handlePermanentDelete = async () => {
    if (!deleteModal.booking) return;
    
    const booking = deleteModal.booking;
    // Only allow delete for completed or cancelled bookings
    if (!['completed', 'cancelled'].includes(booking.status)) {
      toast.error('Only completed or cancelled bookings can be permanently deleted');
      return;
    }
    
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      if (booking.is_ride) {
        await ridesAPI.delete(booking.id);
      } else {
        await bookingsAPI.delete(booking.id, deleteModal.reason || 'Deleted by owner');
      }
      toast.success(`${booking.is_ride ? 'Ride' : 'Booking'} permanently deleted`);
      setDeleteModal({ open: false, booking: null, action: null, reason: '', loading: false });
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Open delete/cancel modal
  const openDeleteModal = (booking, action, e) => {
    if (e) e.stopPropagation();
    setDeleteModal({ 
      open: true, 
      booking, 
      action, 
      reason: '',
      loading: false 
    });
  };

  const handleBulkDeleteBookings = async () => {
    if (!window.confirm(`Cancel ${selectedBookingIds.length} booking(s)? This action cannot be undone.`)) return;
    const reason = prompt('Reason for bulk cancellation:');
    if (!reason) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      for (const id of selectedBookingIds) {
        await bookingsAPI.manage(id, { status: 'cancelled', reason, notify_customer: true });
      }
      toast.success(`${selectedBookingIds.length} booking(s) cancelled`);
      setSelectedBookingIds([]);
      setBulkSelectMode(prev => ({ ...prev, bookings: false }));
      fetchAllData();
    } catch (e) {
      toast.error('Failed to cancel some bookings');
    }
  };

  // Filter bookings based on selected sub-tab and search
  const filteredBookings = bookings.filter(b => {
    // Status filter
    let statusMatch = true;
    if (bookingStatusTab === 'all') statusMatch = true;
    else if (bookingStatusTab === 'rides') statusMatch = b.is_ride === true;
    else if (bookingStatusTab === 'urgent') statusMatch = b.is_urgent;
    else if (bookingStatusTab === 'new') statusMatch = b.status === 'pending';
    else if (bookingStatusTab === 'needs_worker') statusMatch = b.status === 'confirmed' && !b.worker_id;
    else if (bookingStatusTab === 'assigned') statusMatch = b.status === 'confirmed' && b.worker_id;
    else if (bookingStatusTab === 'in_progress') statusMatch = b.status === 'in_progress';
    else if (bookingStatusTab === 'completed') statusMatch = b.status === 'completed';
    
    // Search filter
    if (!statusMatch) return false;
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const searchUpper = searchTerm.toUpperCase();
    return (
      b.reference_number?.toUpperCase().includes(searchUpper) ||
      b.id?.toLowerCase().startsWith(search) ||
      b.service_name?.toLowerCase().includes(search) ||
      b.customer_name?.toLowerCase().includes(search) ||
      b.customer_email?.toLowerCase().includes(search) ||
      b.worker_name?.toLowerCase().includes(search) ||
      b.address?.toLowerCase().includes(search) ||
      b.city?.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500',
      confirmed: 'bg-[#D4AF37]/100',
      in_progress: 'bg-[#F4C2C2]/100',
      completed: 'bg-emerald-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-[var(--background-subtle)]0';
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      confirmed: 'bg-[#D4AF37]/10 text-[#D4AF37] border-blue-300',
      in_progress: 'bg-[#F4C2C2]/15 text-purple-700 border-purple-300',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      cancelled: 'bg-[var(--error)]/15 text-red-700 border-red-300'
    };
    return styles[status] || 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-[var(--border-primary)]';
  };

  const openDetailModal = (booking) => {
    setDetailModalBooking(booking);
  };

  const handleArchiveBooking = async (bookingId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Archive this booking? It will be moved to the archive section.')) return;
    try {
      await api.post(`/bookings/${bookingId}/archive`);
      toast.success('Booking archived');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to archive booking');
    }
  };

  const handleOpenEditModal = (booking, e) => {
    e?.stopPropagation();
    setEditData({
      scheduled_date: booking.scheduled_date || '',
      scheduled_time: booking.scheduled_time || '',
      duration_hours: booking.duration_hours || 2,
      address: booking.address || '',
      city: booking.city || '',
      state: booking.state || '',
      zip_code: booking.zip_code || '',
      special_instructions: booking.special_instructions || '',
      is_urgent: booking.is_urgent || false
    });
    setEditModal({ open: true, booking });
  };

  const handleSaveBooking = async () => {
    try {
      await api.put(`/bookings/${editModal.booking.id}/edit`, editData);
      toast.success('Booking updated');
      setEditModal({ open: false, booking: null });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">All Bookings</h1>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search ref #, name, email..."
                className="pl-10 pr-4 py-2.5 border border-[var(--border-primary)] rounded-lg w-full sm:w-56 focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-gray-500"
                data-testid="bookings-search"
              />
            </div>
            {/* Bulk Select Toggle */}
            <Button 
              variant={bulkSelectMode.bookings ? "default" : "outline"}
              onClick={() => {
                setBulkSelectMode(prev => ({ ...prev, bookings: !prev.bookings }));
                setSelectedBookingIds([]);
              }}
              className={bulkSelectMode.bookings ? "bg-rose-600 text-white" : ""}
              data-testid="bookings-bulk-select-toggle"
            >
              {bulkSelectMode.bookings ? "Cancel Selection" : "Select Multiple"}
            </Button>
            {bulkSelectMode.bookings && selectedBookingIds.length > 0 && (
              <Button 
                onClick={handleBulkDeleteBookings}
                className="bg-[var(--error)] hover:bg-[var(--error)]/80 text-white"
                data-testid="bookings-bulk-cancel"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel ({selectedBookingIds.length})
              </Button>
            )}
            <Button onClick={() => setShowCreateBookingModal(true)} className="btn-primary" data-testid="create-booking-btn">
              <Plus className="w-5 h-5 mr-2" />
              Create Booking
            </Button>
            <Button onClick={() => setShowCreateRideModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-ride-btn">
              <Car className="w-5 h-5 mr-2" />
              Create Ride
            </Button>
          </div>
        </div>
      </div>
      
      {/* Status Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'all', label: 'All', count: bookings.length },
          { id: 'rides', label: 'Rides', count: bookings.filter(b => b.is_ride).length, ride: true },
          { id: 'urgent', label: 'Urgent', count: bookings.filter(b => b.is_urgent).length, urgent: true },
          { id: 'new', label: 'New', count: bookings.filter(b => b.status === 'pending').length },
          { id: 'needs_worker', label: 'Needs Worker', count: bookings.filter(b => b.status === 'confirmed' && !b.worker_id).length },
          { id: 'assigned', label: 'Assigned', count: bookings.filter(b => b.status === 'confirmed' && b.worker_id).length },
          { id: 'in_progress', label: 'In Progress', count: bookings.filter(b => b.status === 'in_progress').length },
          { id: 'completed', label: 'Completed', count: bookings.filter(b => b.status === 'completed').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setBookingStatusTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              bookingStatusTab === tab.id 
                ? tab.urgent ? 'bg-amber-500 text-white' : tab.ride ? 'bg-blue-600 text-white' : 'bg-[var(--primary)] text-white' 
                : tab.urgent ? 'bg-[var(--warning)]/10 text-amber-700 hover:bg-amber-100 border border-amber-300' : tab.ride ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300' : 'bg-[var(--accent)] text-[var(--text-secondary)] hover:bg-[var(--primary)]/10'
            }`}
            data-testid={`booking-tab-${tab.id}`}
          >
            {tab.urgent && <AlertTriangle className="w-4 h-4" />}
            {tab.label}
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              bookingStatusTab === tab.id ? 'bg-[var(--background-paper)]/20' : tab.urgent ? 'bg-amber-200' : 'bg-[var(--background)]'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>
      
      {filteredBookings.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">No {bookingStatusTab === 'all' ? '' : bookingStatusTab.replace('_', ' ')} bookings</p>
        </div>
      ) : (
        <>
          {/* Select All / Clear */}
          {bulkSelectMode.bookings && (
            <div className="mb-4 flex flex-wrap items-center gap-4 p-3 bg-[var(--primary)]/20 border border-[var(--primary)]/40 rounded-lg">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedBookingIds(filteredBookings.map(b => b.id))}
              >
                Select All ({filteredBookings.length})
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedBookingIds([])}
              >
                Clear Selection
              </Button>
              {/* Batch Assign to Me Button */}
              {handleBatchAssignToOwner && selectedBookingIds.length > 0 && (
                <Button 
                  size="sm"
                  className="bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white"
                  onClick={() => {
                    // Filter to only unassigned bookings
                    const unassignedIds = selectedBookingIds.filter(id => {
                      const booking = filteredBookings.find(b => b.id === id);
                      return booking && !booking.worker_id;
                    });
                    if (unassignedIds.length > 0) {
                      handleBatchAssignToOwner(unassignedIds);
                    } else {
                      toast.info('All selected bookings already have workers assigned');
                    }
                  }}
                >
                  <User className="w-4 h-4 mr-1" />
                  Assign {selectedBookingIds.length} to Me
                </Button>
              )}
              <span className="text-sm text-[var(--primary)] ml-auto font-medium">
                {selectedBookingIds.length} selected
              </span>
            </div>
          )}

          {/* Overview Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredBookings.map(booking => (
              <motion.div
                key={booking.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`card overflow-hidden cursor-pointer hover:shadow-lg transition-all ${
                  bulkSelectMode.bookings && selectedBookingIds.includes(booking.id) 
                    ? 'ring-2 ring-[var(--primary)] bg-[var(--primary)]/10' 
                    : 'hover:scale-[1.02]'
                }`}
                onClick={() => !bulkSelectMode.bookings && openDetailModal(booking)}
                data-testid={`booking-card-${booking.id}`}
              >
                {/* Status Bar */}
                <div className={`h-1.5 ${getStatusColor(booking.status)}`} />
                
                {/* Bulk Select Checkbox */}
                {bulkSelectMode.bookings && (
                  <div className="absolute top-3 right-3 z-10">
                    <Checkbox
                      checked={selectedBookingIds.includes(booking.id)}
                      onCheckedChange={() => toggleBookingSelection(booking.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5"
                    />
                  </div>
                )}

                <div className="p-4">
                  {/* Reference Number Badge */}
                  <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mb-2">
                    <Hash className="w-3 h-3" />
                    <span className="font-mono font-medium text-[var(--primary)]">{booking.reference_number || booking.id?.slice(0, 8).toUpperCase()}</span>
                  </div>

                  {/* Header with Service & Urgent Badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[var(--text-primary)] truncate">{booking.service_name}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadgeStyle(booking.status)}`}>
                          {booking.status?.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                          booking.payment_status === 'paid' 
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                            : 'bg-amber-100 text-amber-700 border-amber-300'
                        }`}>
                          {booking.payment_status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {booking.is_urgent && (
                      <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shrink-0 ml-2">
                        <AlertTriangle className="w-3 h-3" />
                        URGENT
                      </span>
                    )}
                  </div>

                  {/* Key Info Grid */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate">{booking.customer_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span>{booking.scheduled_date}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span>{booking.scheduled_time} ({booking.duration_hours}h)</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate">{booking.city}, {booking.state}</span>
                    </div>
                  </div>

                  {/* Worker Assignment - Supports multiple workers */}
                  {(booking.workers && booking.workers.length > 0) || booking.worker_name ? (
                    <div className="bg-[#D4AF37]/10 rounded-lg px-3 py-2 mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <User className="w-4 h-4 text-[#D4AF37]" />
                        {booking.workers && booking.workers.length > 0 ? (
                          <>
                            {booking.workers.map((w, idx) => (
                              <span key={w.id || idx} className="text-sm text-[#D4AF37] font-medium">
                                {w.name}{w.role === 'primary' && ' (Lead)'}{idx < booking.workers.length - 1 && ','}
                              </span>
                            ))}
                            {booking.workers.length > 1 && (
                              <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full ml-1">
                                {booking.workers.length} workers
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-[#D4AF37] font-medium">{booking.worker_name}</span>
                        )}
                      </div>
                      {/* Add more workers button */}
                      {handleAssignToOwner && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedBooking(booking); setShowAssignModal(true); }}
                          className="text-xs text-[#D4AF37] hover:underline mt-1 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add another worker
                        </button>
                      )}
                    </div>
                  ) : booking.status === 'confirmed' && (
                    <div className="bg-[var(--warning)]/10 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-700">Needs worker assignment</span>
                    </div>
                  )}

                  {/* Add-ons Preview */}
                  {booking.selected_addons && booking.selected_addons.length > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-[#F4C2C2] mb-3">
                      <Package className="w-3.5 h-3.5" />
                      <span>{booking.selected_addons.length} add-on(s)</span>
                    </div>
                  )}

                  {/* Mileage Info */}
                  {booking.distance_miles > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-blue-600 mb-3">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{booking.distance_miles?.toFixed(1)} miles{booking.mileage_fee > 0 && ` (+$${booking.mileage_fee?.toFixed(2)} fee)`}</span>
                    </div>
                  )}

                  {/* Footer: Price & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-[var(--primary)]">
                        ${booking.total_amount?.toFixed(2)}
                      </span>
                      <NoteIconButton
                        entityType="booking"
                        entityId={booking.id}
                        entityName={`${booking.service_name} - ${booking.customer_name || booking.customer_email}`}
                        noteCount={noteCounts[`booking_${booking.id}`] || 0}
                        onOpen={(type, id, name) => {
                          setNotesModal({ open: true, entityType: type, entityId: id, entityName: name });
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleOpenEditModal(booking, e)}
                        className="text-[var(--text-muted)] hover:text-[#D4AF37]"
                        title="Edit"
                        data-testid={`edit-booking-${booking.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleOpenReschedule(booking); }}
                        className="text-[var(--text-muted)] hover:text-[#F4C2C2]"
                        title="Reschedule"
                        data-testid={`reschedule-booking-${booking.id}`}
                      >
                        <CalendarClock className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewBookingDetails(booking.id);
                        }}
                        className="text-[var(--text-muted)] hover:text-[var(--primary)]"
                        data-testid={`view-details-${booking.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {/* Cancel button - for active bookings */}
                      {!['completed', 'cancelled'].includes(booking.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => openDeleteModal(booking, 'cancel', e)}
                          className="text-[var(--text-muted)] hover:text-amber-500"
                          title="Cancel"
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Delete button - for completed/cancelled bookings */}
                      {['completed', 'cancelled'].includes(booking.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => openDeleteModal(booking, 'delete', e)}
                          className="text-[var(--text-muted)] hover:text-red-500"
                          title="Delete Permanently"
                          data-testid={`delete-booking-${booking.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleArchiveBooking(booking.id, e)}
                        className="text-[var(--text-muted)] hover:text-[var(--error)]"
                        title="Archive"
                        data-testid={`archive-booking-${booking.id}`}
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Quick Actions (if not in bulk mode) */}
                  {!bulkSelectMode.bookings && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                      {booking.status === 'pending' && (
                        <>
                          <Button 
                            size="sm"
                            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                            onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.id, 'confirmed'); }}
                            data-testid={`accept-booking-${booking.id}`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Accept
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="flex-1 text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/10 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.id, 'cancelled', true); }}
                            data-testid={`reject-booking-${booking.id}`}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {booking.status === 'confirmed' && !booking.worker_id && (
                        <>
                          <Button 
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={(e) => { e.stopPropagation(); setSelectedBooking(booking); setShowAssignModal(true); }}
                            data-testid={`assign-worker-${booking.id}`}
                          >
                            <User className="w-3 h-3 mr-1" />
                            Assign Worker
                          </Button>
                          {handleAssignToOwner && (
                            <Button 
                              size="sm"
                              className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white text-xs"
                              onClick={(e) => { e.stopPropagation(); handleAssignToOwner(booking); }}
                              data-testid={`assign-to-me-${booking.id}`}
                            >
                              <User className="w-3 h-3 mr-1" />
                              Assign to Me
                            </Button>
                          )}
                        </>
                      )}
                      {booking.status === 'confirmed' && booking.worker_id && (
                        <Button 
                          size="sm"
                          className="flex-1 bg-[#D4AF37]/100 hover:bg-[#E6C965] text-white text-xs"
                          onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.id, 'in_progress'); }}
                          data-testid={`start-booking-${booking.id}`}
                        >
                          Start Job
                        </Button>
                      )}
                      {booking.status === 'in_progress' && (
                        <Button 
                          size="sm"
                          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                          onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.id, 'completed'); }}
                          data-testid={`complete-booking-${booking.id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* Booking Detail Modal */}
      <BookingDetailModal
        isOpen={!!detailModalBooking}
        onClose={() => setDetailModalBooking(null)}
        booking={detailModalBooking}
        onRefresh={fetchAllData}
      />

      {/* Edit Booking Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditModal({ open: false, booking: null })}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Edit Booking</h2>
            <p className="text-sm text-[var(--text-muted)] mb-4">Booking ID: {editModal.booking?.id?.slice(0, 8)}</p>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={editData.scheduled_date || ''}
                    onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <input
                    type="time"
                    value={editData.scheduled_time || ''}
                    onChange={(e) => setEditData({ ...editData, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Duration (hours)</label>
                <input
                  type="number"
                  value={editData.duration_hours || 2}
                  onChange={(e) => setEditData({ ...editData, duration_hours: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0.5"
                  step="0.5"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  type="text"
                  value={editData.address || ''}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    value={editData.city || ''}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    type="text"
                    value={editData.state || ''}
                    onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP</label>
                  <input
                    type="text"
                    value={editData.zip_code || ''}
                    onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Special Instructions</label>
                <textarea
                  value={editData.special_instructions || ''}
                  onChange={(e) => setEditData({ ...editData, special_instructions: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_urgent"
                  checked={editData.is_urgent || false}
                  onChange={(e) => setEditData({ ...editData, is_urgent: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_urgent" className="text-sm">Mark as Urgent</label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditModal({ open: false, booking: null })} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveBooking} className="flex-1 btn-primary">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete/Cancel Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.open && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => !deleteModal.loading && setDeleteModal({ open: false, booking: null, action: null, reason: '', loading: false })}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--card)] rounded-xl p-6 w-full max-w-md border border-[var(--border)] shadow-2xl"
              onClick={e => e.stopPropagation()}
              data-testid="delete-confirm-modal"
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center ${
                deleteModal.action === 'delete' ? 'bg-red-500/20' : 'bg-amber-500/20'
              }`}>
                {deleteModal.action === 'delete' ? (
                  <Trash2 className="w-7 h-7 text-red-500" />
                ) : (
                  <Ban className="w-7 h-7 text-amber-500" />
                )}
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-center mb-2 text-[var(--text-primary)]">
                {deleteModal.action === 'delete' ? 'Delete Permanently?' : 'Cancel Booking?'}
              </h2>
              
              {/* Description */}
              <p className="text-center text-[var(--text-muted)] text-sm mb-4">
                {deleteModal.action === 'delete' 
                  ? 'This will permanently remove this record from the database. This action cannot be undone.'
                  : 'This will cancel the booking and notify the customer. The record will be kept for your history.'}
              </p>

              {/* Booking Info */}
              {deleteModal.booking && (
                <div className="bg-[var(--background)] rounded-lg p-3 mb-4 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[var(--text-muted)]">Reference:</span>
                    <span className="font-mono font-medium text-[var(--primary)]">
                      {deleteModal.booking.reference_number || deleteModal.booking.id?.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[var(--text-muted)]">Service:</span>
                    <span className="text-[var(--text-primary)]">{deleteModal.booking.service_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-muted)]">Customer:</span>
                    <span className="text-[var(--text-primary)]">{deleteModal.booking.customer_name || 'N/A'}</span>
                  </div>
                  {deleteModal.booking.is_ride && (
                    <div className="flex items-center gap-1 mt-2 text-purple-400 text-xs">
                      <Car className="w-3 h-3" />
                      <span>This is a ride</span>
                    </div>
                  )}
                </div>
              )}

              {/* Reason input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                  Reason {deleteModal.action === 'cancel' && <span className="text-red-400">*</span>}
                </label>
                <textarea
                  value={deleteModal.reason}
                  onChange={(e) => setDeleteModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={deleteModal.action === 'delete' ? 'Optional reason...' : 'Enter reason for cancellation...'}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--background)] text-[var(--text-primary)] text-sm focus:ring-2 focus:ring-[var(--primary)]/50 focus:border-[var(--primary)]"
                  rows={2}
                  disabled={deleteModal.loading}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteModal({ open: false, booking: null, action: null, reason: '', loading: false })}
                  className="flex-1"
                  disabled={deleteModal.loading}
                >
                  Keep It
                </Button>
                <Button 
                  onClick={deleteModal.action === 'delete' ? handlePermanentDelete : handleCancelBooking}
                  className={`flex-1 ${
                    deleteModal.action === 'delete' 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                  disabled={deleteModal.loading || (deleteModal.action === 'cancel' && !deleteModal.reason.trim())}
                  data-testid="confirm-delete-btn"
                >
                  {deleteModal.loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : deleteModal.action === 'delete' ? (
                    <>
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Forever
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel Booking
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default BookingsTab;
